import React from 'react'
import { useSnackbar, SnackbarContent } from 'notistack'
import { useHistory } from 'react-router'
import { CloseCircle } from 'iconsax-react'

import AvatarGroup from '../AvatarGroup'
import MediaAvatar from '../../atoms/MediaAvatar'
import TaskNotificationIcon from '../../atoms/TaskNotificationIcon'

import { Routes } from '@SonarCX/Routes'
import { TYPES } from '@SonarCX/providers/AppNotificationProvider'
import { replaceMentions } from '@SonarCX/utilities'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'

import useStyles from './styles'

const AppNotification = (
  { id, type, message = '', users = [], name = '', msg, onClick, isGroup },
  ref
) => {
  const { closeSnackbar } = useSnackbar()
  const history = useHistory()

  const { currentUser } = useAgent()
  const { allUsers, getUsers } = useUserAPI()

  const mentioned = React.useMemo(() => msg.mentioned ?? [], [msg.mentioned])

  const fullUsers = React.useMemo(() => {
    const others = users.filter(id => id !== currentUser()?.id)
    return others.map(id => allUsers[id]).filter(Boolean)
  }, [users, currentUser, allUsers])

  const title = React.useMemo(() => {
    if (name) {
      if (typeof name === 'string') {
        return replaceMentions(name, Object.values(getUsers(false)), false)
      }

      return name
    }

    const userNames = fullUsers.map(user => user.name).join(', ')
    if (userNames.length) {
      return userNames
    }

    // Fallback if all of the session users are not loaded
    return 'Guest'
  }, [name, fullUsers, getUsers])

  const cleanedMessage = React.useMemo(() => {
    if (typeof message === 'string') {
      const agents = Object.values(getUsers(false))
      const combined = [...mentioned, ...agents]
      return replaceMentions(message, combined)
    }

    return message
  }, [message, getUsers, mentioned])

  const classes = useStyles({ type })

  const handleClose = React.useCallback(
    e => {
      e.stopPropagation()
      closeSnackbar(id)
    },
    [closeSnackbar, id]
  )

  const handleClick = React.useCallback(
    e => {
      handleClose(e)
      onClick && onClick()

      if (type === TYPES.REMINDER) {
        // We load the reminders per month and not on login, so we need to redirect user to the planner
        history.replace(`${Routes.DASHBOARD}?tab=planner`)
        return
      }

      if (type === TYPES.NOTIFICATION) {
        const { source_session_id, source_id, source } = msg
        if (source === 'task') {
          history.replace(`${Routes.TASKS}/${source_id}`)
        } else {
          const route = isGroup ? Routes.PEER_CHAT : Routes.CLIENT_CHATS
          const idBlock = isGroup ? '' : `&id=${source_id}`
          const change = isGroup ? '' : '&change=true'
          sessionStorage.removeItem('last_convo')
          history.replace(
            `${route}?session=${source_session_id}${idBlock}${change}`
          )
        }
        return
      }

      if (type === TYPES.NEW_CHAT) {
        history.replace(
          `${Routes.CLIENT_CHATS}?session=${msg.session}&change=true`
        )
        return
      }

      if (type === TYPES.SURVEY) {
        const { sessionId } = msg
        history.replace(`${Routes.CLIENT_CHATS}?session=${sessionId}`)
        return
      }

      const isConvo = type === TYPES.CONVERSATION
      const route = isConvo ? Routes.CLIENT_CHATS : Routes.PEER_CHAT

      let session = ''
      if ([TYPES.CONVERSATION, TYPES.GROUP].includes(type)) {
        session = msg.sessionId
      } else {
        session = msg.content.user.id
      }

      const isUser = type === TYPES.PEER ? '&isUser' : ''

      sessionStorage.removeItem('last_convo')
      history.replace(`${route}?session=${session}${isUser}`)
    },
    [msg, type, history, handleClose, onClick, isGroup]
  )

  return (
    <SnackbarContent ref={ref} className={classes.root}>
      <div onClick={handleClick} className={classes.content}>
        <div className={classes.type} />

        {msg.source === 'task' ? (
          <div className={classes.avatarArea}>
            <div className={classes.taskIcon}>
              <TaskNotificationIcon />
            </div>
          </div>
        ) : type === TYPES.NEW_CHAT ? (
          <AvatarGroup className={classes.avatarArea}>
            <MediaAvatar
              user={{ name: '..', id: users[0] ?? '' }}
              showBadge
              group
              activity={'online'}
            />
          </AvatarGroup>
        ) : (
          <AvatarGroup className={classes.avatarArea}>
            {fullUsers.map(user => (
              <MediaAvatar key={user.id} user={user} showBadge group />
            ))}
          </AvatarGroup>
        )}

        <div className={classes.messageArea}>
          <div className={classes.title}>{title}</div>

          <div className={classes.message}>{cleanedMessage}</div>
        </div>

        <div className={classes.closeArea}>
          <CloseCircle onClick={handleClose} className='icon' />
        </div>
      </div>
    </SnackbarContent>
  )
}

export default React.forwardRef(AppNotification)
